<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="t('messages.transactions.ad_link.archive_title')"
    :style="{ width: '60rem', maxWidth: '96vw' }"
    :breakpoints="{ '768px': '96vw' }"
    data-testid="ad-archive-dialog"
    @show="load"
  >
    <!-- Header per the RED-278 prototype: title · outlined portal tag · archived-at · version select right -->
    <template #header>
      <div class="flex flex-wrap items-center gap-3">
        <span class="p-dialog-title">{{ t('messages.transactions.ad_link.archive_title') }}</span>
        <span v-if="archive?.portal" class="rounded border border-primary px-2 py-0.5 text-xs text-primary">{{ archive.portal }}</span>
        <span v-if="selectedVersion" class="text-sm text-muted-color">
          {{ t('messages.transactions.ad_link.archived_at', { date: formatDate(selectedVersion.archived_at) }) }}
        </span>
        <Select
          v-if="archive?.versions?.length > 1"
          v-model="selectedVersionNumber"
          :options="archive.versions"
          option-value="version"
          :option-label="versionLabel"
          size="small"
          class="ml-auto"
          data-testid="ad-archive-version"
        />
      </div>
    </template>

    <div v-if="loading && !archive" class="py-8 text-center text-muted-color">
      <i class="pi pi-spin pi-spinner mr-2"></i>{{ t('messages.transactions.ad_link.archive_loading') }}
    </div>

    <Message v-else-if="loadError" severity="error" :closable="false">
      {{ t('messages.transactions.ad_link.archive_load_error') }}
    </Message>

    <template v-else-if="archive">
      <!-- No copy yet: the state IS the content (prototype hero blocks, admin action inside) -->
      <div v-if="hero" class="max-w-xl py-8" :data-testid="hero.testid">
        <i :class="[hero.icon, 'text-2xl', hero.iconClass]"></i>
        <h3 class="mt-3 mb-1 text-lg font-semibold" :class="hero.titleClass">{{ hero.title }}</h3>
        <p class="text-muted-color">{{ hero.body }}</p>
        <p v-if="hero.detail" class="mt-1 text-sm text-muted-color">{{ hero.detail }}</p>
        <Button
          v-if="adminAction?.placement === 'hero'"
          :label="adminAction.label"
          :icon="retrying ? 'pi pi-spin pi-spinner' : undefined"
          outlined
          class="mt-4"
          :disabled="retrying"
          data-testid="ad-archive-admin-action"
          @click="retry"
        />
      </div>

      <!-- Archived copy -->
      <template v-if="selectedVersion">
        <!-- A newer attempt on top of an existing copy: one slim line, the copy stays the hero -->
        <Message v-if="status === 'pending'" severity="secondary" :closable="false" icon="pi pi-box" class="mb-3" data-testid="ad-archive-status">
          {{ t('messages.transactions.ad_link.archive_pending') }}
          <span class="block text-sm">{{ archive.attempt?.error_message || t('messages.transactions.ad_link.archive_pending_body') }}</span>
        </Message>
        <Message v-else-if="status === 'failed'" severity="warn" :closable="false" icon="pi pi-exclamation-triangle" class="mb-3" data-testid="ad-archive-status">
          {{ t('messages.transactions.ad_link.archive_failed') }}
          <span v-if="archive.attempt?.attempted_at" class="block text-sm">
            {{ t('messages.transactions.ad_link.archive_failed_body', { date: formatDate(archive.attempt.attempted_at) }) }}
            <template v-if="archive.attempt.error_message"> · {{ archive.attempt.error_message }}</template>
          </span>
        </Message>

        <Message
          :severity="selectedVersion.matches_current_link ? 'success' : 'warn'"
          :icon="selectedVersion.matches_current_link ? 'pi pi-check' : 'pi pi-exclamation-circle'"
          :closable="false"
          class="mb-3"
          data-testid="ad-archive-match"
        >
          <template v-if="selectedVersion.matches_current_link">
            {{ t('messages.transactions.ad_link.archive_match') }}
          </template>
          <template v-else>
            {{ t('messages.transactions.ad_link.archive_mismatch') }}
            <a
              v-if="archive.current_link"
              :href="archive.current_link"
              target="_blank"
              rel="noopener noreferrer"
              class="break-all underline"
            >{{ archive.current_link }}</a>
          </template>
        </Message>

        <Message v-if="olderThanLatest" severity="secondary" icon="pi pi-history" :closable="false" class="mb-3" data-testid="ad-archive-older">
          {{ t('messages.transactions.ad_link.archive_older_version') }}
          <a href="#" class="text-primary hover:text-primary-600" @click.prevent="selectedVersionNumber = archive.latest.version">
            {{ versionLabel(archive.latest) }}
          </a>
        </Message>

        <div class="text-sm mb-3 flex flex-wrap items-baseline gap-2" data-testid="ad-archive-provenance">
          <span class="text-muted-color">{{ t('messages.transactions.ad_link.archive_link_label') }}</span>
          <a
            v-if="!archivedLinkDead"
            :href="selectedVersion.archived_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary hover:text-primary-600 break-all"
          >
            {{ selectedVersion.archived_url }} <span class="pi pi-external-link text-[10px]"></span>
          </a>
          <template v-else>
            <span class="line-through text-muted-color break-all">{{ selectedVersion.archived_url }}</span>
            <Tag :value="t('messages.transactions.ad_link.archive_link_dead')" severity="warn" />
          </template>
        </div>

        <div v-if="selectedVersion.title || priceParts" class="mb-4" data-testid="ad-archive-headline">
          <h3 v-if="selectedVersion.title" class="text-xl font-semibold leading-snug">{{ selectedVersion.title }}</h3>
          <div v-if="priceParts" class="mt-1 flex flex-wrap items-baseline gap-2">
            <span class="text-xl font-bold">{{ priceParts.main }}</span>
            <span v-if="priceParts.secondary" class="text-muted-color">{{ priceParts.secondary }}</span>
          </div>
        </div>

        <!-- RED-306: the picture rides its own flag; without it the RED-278 disabled affordance stays as designed -->
        <div class="mb-5 flex flex-wrap items-center gap-3">
          <Button
            :label="t('messages.transactions.ad_link.archive_screenshot')"
            icon="pi pi-image"
            :severity="selectedVersion.screenshot_url ? undefined : 'secondary'"
            outlined
            size="small"
            :disabled="!selectedVersion.screenshot_url"
            data-testid="ad-archive-screenshot-button"
            @click="showScreenshot = true"
          />
          <span class="text-sm text-muted-color" data-testid="ad-archive-screenshot-note">{{ screenshotNote }}</span>
          <Button
            v-if="adminAction?.placement === 'screenshot'"
            :label="adminAction.label"
            :icon="retrying ? 'pi pi-spin pi-spinner' : 'pi pi-camera'"
            outlined
            size="small"
            :disabled="retrying || status === 'pending'"
            data-testid="ad-archive-admin-action"
            @click="retry"
          />
        </div>

        <Dialog
          v-model:visible="showScreenshot"
          modal
          maximizable
          :header="t('messages.transactions.ad_link.archive_screenshot_title', { date: formatDate(selectedVersion.screenshot_at) })"
          :style="{ width: '82rem', maxWidth: '96vw' }"
          :breakpoints="{ '768px': '96vw' }"
          data-testid="ad-archive-screenshot-dialog"
        >
          <div class="max-h-[80vh] overflow-auto">
            <img
              v-if="selectedVersion.screenshot_url"
              :src="selectedVersion.screenshot_url"
              :alt="t('messages.transactions.ad_link.archive_screenshot_alt')"
              class="w-full h-auto"
              data-testid="ad-archive-screenshot-image"
            />
          </div>
          <template #footer>
            <div class="flex flex-wrap items-center gap-2 w-full">
              <a
                v-if="selectedVersion.screenshot_url"
                :href="selectedVersion.screenshot_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-primary hover:text-primary-600 mr-auto"
              >
                <span class="pi pi-external-link"></span> {{ t('messages.transactions.ad_link.archive_screenshot_open_tab') }}
              </a>
              <Button :label="t('messages.transactions.ad_link.archive_close')" severity="secondary" outlined @click="showScreenshot = false" />
            </div>
          </template>
        </Dialog>

        <!-- Facts: two columns of label · value rows (prototype), not a label-above grid -->
        <dl v-if="factRows.length" class="mb-5 grid grid-cols-1 gap-x-8 text-sm md:grid-cols-2" data-testid="ad-archive-facts">
          <div v-for="fact in factRows" :key="fact.key" class="flex justify-between gap-3 border-b border-surface-200 py-2">
            <dt class="text-muted-color">{{ t(`messages.transactions.ad_link.facts.${fact.key}`) }}</dt>
            <dd class="text-right">{{ fact.value }}</dd>
          </div>
        </dl>

        <!-- eslint-disable-next-line vue/no-v-html -- sanitized markdown (DOMPurify) -->
        <div
          v-if="descriptionHtml"
          class="ad-archive-markdown max-w-3xl text-sm leading-relaxed"
          data-testid="ad-archive-description"
          v-html="descriptionHtml"
        ></div>
      </template>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-center gap-2 w-full">
        <span class="text-xs text-muted-color mr-auto">{{ t('messages.transactions.ad_link.archive_legal') }}</span>
        <Button
          v-if="adminAction?.placement === 'footer'"
          :label="adminAction.label"
          :icon="retrying ? 'pi pi-spin pi-spinner' : undefined"
          outlined
          :disabled="retrying || status === 'pending'"
          data-testid="ad-archive-admin-action"
          @click="retry"
        />
        <Button :label="t('messages.transactions.ad_link.archive_close')" severity="secondary" outlined @click="visible = false" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import { format, parseISO } from 'date-fns'
import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { useToast } from 'primevue/usetoast'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeature } from '@/composables/useFeature'
import { useTransactionStore } from '@/stores/transaction'

const props = defineProps({
  transactionId: { type: [Number, String], default: null },
})
const visible = defineModel('visible', { type: Boolean, default: false })
// Fires with the sidebar-level summary so the "Tehingu kuulutus" block stays in sync.
const emit = defineEmits(['updated'])

const { t } = useI18n()
const page = usePage()
const toast = useToast()
const store = useTransactionStore()

const isAdmin = computed(() => page.props.auth?.can?.admin ?? false)
const screenshotsEnabled = useFeature('ad-archive-screenshot')

const archive = ref(null)
const loading = ref(false)
const loadError = ref(false)
const retrying = ref(false)
const selectedVersionNumber = ref(null)
const showScreenshot = ref(false)

const status = computed(() => archive.value?.status ?? 'none')
const selectedVersion = computed(() => {
  const versions = archive.value?.versions ?? []
  return versions.find(v => v.version === selectedVersionNumber.value) ?? archive.value?.latest ?? null
})
const olderThanLatest = computed(
  () => !!selectedVersion.value && !!archive.value?.latest && selectedVersion.value.version < archive.value.latest.version,
)
// A not_found attempt for the same URL the copy was made from = the archived link is dead.
const archivedLinkDead = computed(
  () =>
    status.value === 'not_found' &&
    !!selectedVersion.value &&
    archive.value?.attempt?.archived_url === selectedVersion.value.archived_url,
)

const formatDate = iso => (iso ? format(parseISO(iso), 'dd.MM.yyyy HH:mm') : '')
const versionLabel = v => t('messages.transactions.ad_link.archive_version', { n: v.version, date: formatDate(v.archived_at) })

// Without a copy the state fills the body (prototype: icon, title, one sentence, admin action).
const hero = computed(() => {
  if (selectedVersion.value) return null
  const attemptedAt = formatDate(archive.value?.attempt?.attempted_at)
  const errorMessage = archive.value?.attempt?.error_message
  switch (status.value) {
    case 'pending':
      return {
        testid: 'ad-archive-status',
        icon: 'pi pi-box',
        iconClass: 'text-muted-color',
        title: t('messages.transactions.ad_link.archive_pending'),
        body: errorMessage || t('messages.transactions.ad_link.archive_pending_body'),
      }
    case 'failed':
      return {
        testid: 'ad-archive-status',
        icon: 'pi pi-exclamation-triangle',
        iconClass: 'text-amber-600',
        titleClass: 'text-amber-800',
        title: t('messages.transactions.ad_link.archive_failed'),
        body: t('messages.transactions.ad_link.archive_failed_body', { date: attemptedAt }),
        detail: errorMessage,
      }
    case 'not_found':
      return {
        testid: 'ad-archive-status',
        icon: 'pi pi-exclamation-triangle',
        iconClass: 'text-amber-600',
        titleClass: 'text-amber-800',
        title: t('messages.transactions.ad_link.archive_not_found'),
        body: t('messages.transactions.ad_link.archive_not_found_body'),
      }
    case 'unsupported':
      return {
        testid: 'ad-archive-status',
        icon: 'pi pi-ban',
        iconClass: 'text-muted-color',
        title: t('messages.transactions.ad_link.archive_unsupported'),
        body: '',
      }
    default:
      return {
        testid: 'ad-archive-empty',
        icon: 'pi pi-box',
        iconClass: 'text-muted-color',
        title: t('messages.transactions.ad_link.archive_none'),
        body: t('messages.transactions.ad_link.archive_none_body'),
      }
  }
})

// kv.ee prices arrive as "295 000 € (3 606 €/m²)" — headline number + muted per-m² like the prototype.
const priceParts = computed(() => {
  const price = selectedVersion.value?.price
  if (!price) return null
  const match = price.match(/^(.*?)\s*\((.+)\)\s*$/)
  return match ? { main: match[1], secondary: match[2] } : { main: price, secondary: '' }
})

const FACT_KEYS = ['rooms', 'bedrooms', 'floor', 'building_year', 'total_area', 'condition', 'energy_label', 'ownership_type']
const factRows = computed(() => {
  const facts = { ...(selectedVersion.value?.facts ?? {}), condition: selectedVersion.value?.raw_meta_data?.Seisukord ?? null }
  return FACT_KEYS.filter(key => facts[key] !== null && facts[key] !== undefined && facts[key] !== '').map(key => ({
    key,
    value:
      key === 'floor' && facts.total_floors
        ? `${facts.floor} / ${facts.total_floors}`
        : key === 'total_area'
          ? `${facts.total_area} m²`
          : facts[key],
  }))
})

// Flag off: the RED-278 "not archived, feasibility pending" note. Flag on: when
// this copy was pictured, or that it never was (older versions, imports).
const screenshotNote = computed(() => {
  if (!screenshotsEnabled.value) return t('messages.transactions.ad_link.archive_screenshot_note')
  if (selectedVersion.value?.screenshot_url) {
    return t('messages.transactions.ad_link.archive_screenshot_at', { date: formatDate(selectedVersion.value.screenshot_at) })
  }
  return t('messages.transactions.ad_link.archive_screenshot_none')
})

const descriptionHtml = computed(() => {
  const md = selectedVersion.value?.description_md
  if (!md) return ''
  return DOMPurify.sanitize(marked.parse(md, { async: false, breaks: true }), { USE_PROFILES: { html: true } })
})

// One contextual admin action at a time, placed where the prototype puts it:
// inside the hero block (no copy yet), in the footer (link mismatch), or next
// to the screenshot affordance (RED-306: latest copy has no picture).
const adminAction = computed(() => {
  if (!isAdmin.value || !archive.value?.current_link_supported) return null
  if (!selectedVersion.value) {
    if (status.value === 'failed') return { placement: 'hero', label: t('messages.transactions.ad_link.archive_retry') }
    if (status.value === 'none' || status.value === 'not_found') {
      return { placement: 'hero', label: t('messages.transactions.ad_link.archive_now') }
    }
    return null
  }
  if (status.value === 'failed') return { placement: 'footer', label: t('messages.transactions.ad_link.archive_retry') }
  if (!selectedVersion.value.matches_current_link) {
    return { placement: 'footer', label: t('messages.transactions.ad_link.archive_current_link') }
  }
  // A re-run of unchanged content adds the picture without a new version (content-hash dedupe).
  if (screenshotsEnabled.value && !selectedVersion.value.screenshot_url && !olderThanLatest.value) {
    return { placement: 'screenshot', label: t('messages.transactions.ad_link.archive_screenshot_take') }
  }
  return null
})

const summaryOf = data => ({
  status: data.status,
  archived_at: data.latest?.archived_at ?? null,
  matches_current_link: data.latest ? data.latest.matches_current_link : null,
  versions_count: data.versions?.length ?? 0,
})

let pollTimer = null
const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
}
// A queued run takes 20-70 s (politeness delay + fetch); poll gently while the dialog is open.
const schedulePoll = () => {
  stopPolling()
  if (status.value === 'pending' && visible.value) {
    pollTimer = setTimeout(() => load(), 10000)
  }
}

const apply = data => {
  archive.value = data
  selectedVersionNumber.value = data.latest?.version ?? null
  emit('updated', summaryOf(data))
  schedulePoll()
}

const load = async () => {
  if (!props.transactionId) return
  loading.value = true
  loadError.value = false
  try {
    apply(await store.fetchAdArchive(props.transactionId))
  } catch {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

const retry = async () => {
  retrying.value = true
  try {
    apply(await store.retryAdArchive(props.transactionId))
    toast.add({ severity: 'info', summary: t('messages.transactions.ad_link.archive_queued'), life: 4000 })
  } catch {
    toast.add({ severity: 'error', summary: t('messages.transactions.ad_link.archive_retry_error'), life: 5000 })
  } finally {
    retrying.value = false
  }
}

watch(visible, open => {
  if (!open) {
    stopPolling()
    showScreenshot.value = false
  }
})
watch(() => props.transactionId, () => {
  archive.value = null
  selectedVersionNumber.value = null
  showScreenshot.value = false
})
onBeforeUnmount(stopPolling)
</script>

<style scoped>
@reference '../../../css/tailwind.css';

.ad-archive-markdown :deep(h1),
.ad-archive-markdown :deep(h2),
.ad-archive-markdown :deep(h3) {
  @apply font-semibold text-base mt-4 mb-1;
}
.ad-archive-markdown :deep(p) {
  @apply mb-3;
}
.ad-archive-markdown :deep(ul) {
  @apply list-disc pl-5 mb-3;
}
.ad-archive-markdown :deep(strong) {
  @apply font-semibold;
}
</style>
